'use client'

import React from 'react'
import ContactUsForm from './ContactUsForm'
import styles from './contactUs.module.scss'

/**
 * CMS-driven Contact Info types
 */
export interface ContactPhone {
  label?: string
  number: string
  tel: string
}

export interface ContactInfoData {
  businessHours: {
    days: string
    hours: string
  }
  phones: ContactPhone[]
  fax?: {
    label: string
    number: string
  } | null
  email: {
    address: string
    mailto: string
  }
  address: {
    line1: string
    line2: string
  }
}

export interface ContactFormConfig {
  fields?: string[]
  submitLabel?: string
  phonePlaceholder?: string
  phoneDefaultCountry?: string
  phonePrefix?: string
}

export interface ContactUsCmsData {
  page?: string
  brand?: string
  title?: string
  description?: string
  contactInfo: ContactInfoData
  form?: ContactFormConfig
}

interface ContactUsProps {
  cmsData: ContactUsCmsData
}

/**
 * ContactUs — CMS-driven contact page component.
 *
 * Renders:
 * 1. Contact form (dynamic - handles validation & submission)
 * 2. Contact info from CMS config (static content)
 */
const ContactUs: React.FC<ContactUsProps> = ({ cmsData }) => {
  const { contactInfo, form } = cmsData

  return (
    <section className={styles.contactUsPage}>
      {/* Contact Form - handles dynamic functionality */}
      <ContactUsForm
        phonePrefix={form?.phonePrefix}
        submitLabel={form?.submitLabel}
      />

      {/* Contact Info - CMS-driven static content */}
      <div className={styles.contactInfoSection}>
        {/* Business Hours */}
        {contactInfo.businessHours && (
          <div className={styles.businessHours}>
            <p className={styles.hoursDay}>{contactInfo.businessHours.days}</p>
            <p className={styles.hoursTime}>{contactInfo.businessHours.hours}</p>
          </div>
        )}

        {/* Phone Numbers */}
        {contactInfo.phones?.map((phone, idx) => (
          <p key={idx} className={styles.phoneLine}>
            {phone.label && <span className={styles.phoneLabel}>{phone.label}&nbsp;&nbsp;</span>}
            <a href={`tel:${phone.tel}`} className={styles.phoneLink}>
              {phone.number}
            </a>
          </p>
        ))}

        {/* Fax */}
        {contactInfo.fax && (
          <p className={styles.faxLine}>
            <span className={styles.faxLabel}>{contactInfo.fax.label}&nbsp;&nbsp;</span>
            {contactInfo.fax.number}
          </p>
        )}

        {/* Email */}
        {contactInfo.email && (
          <div className={styles.emailSection}>
            <a href={contactInfo.email.mailto} className={styles.emailLink}>
              {contactInfo.email.address}
            </a>
          </div>
        )}

        {/* Address */}
        {contactInfo.address && (
          <div className={styles.addressSection}>
            <p className={styles.addressLine}>{contactInfo.address.line1}</p>
            <p className={styles.addressLine}>{contactInfo.address.line2}</p>
          </div>
        )}
      </div>
    </section>
  )
}

export default ContactUs
